import { useCallback, useEffect, useRef, useState } from 'react';
import apiClient from '../../../services/api';
import { useTheme } from '../theme/ThemeContext';
import {
  haversineMeters,
  pointInFarmGeometry,
} from '../utils/geometry/pointInPolygon';
import type { LiveLocationUpdate } from '../utils/hooks/useMapControls';

export type VegetationAnswer = 'healthy' | 'average' | 'stressed' | 'bare' | 'skip';
export type MoistureAnswer = 'wet' | 'ok' | 'dry' | 'skip';

export type FarmWalkStop = {
  id: string;
  lon: number;
  lat: number;
  at: string;
  insideFarm: boolean;
  vegetation?: VegetationAnswer;
  moisture?: MoistureAnswer;
  satellite: {
    index: string;
    value: number | null;
    ndvi: number | null;
    ndmi: number | null;
    observationDate: string;
    firstImageDate?: string | null;
    message?: string | null;
  };
};

export type FarmWalkReport = {
  type: 'farm_index_walk';
  farmId?: string | number | null;
  farmName?: string | null;
  startedAt: string;
  endedAt: string;
  observationDate: string;
  index: string;
  satellite: string;
  indexMap: {
    tileUrl?: string | null;
    firstImageDate?: string | null;
    visParams?: any;
    spatialResolution?: string | null;
  };
  stops: FarmWalkStop[];
  summary: {
    stopCount: number;
    answeredCount: number;
    vegetation: Record<string, number>;
    moisture: Record<string, number>;
  };
  /** Farmer answers only — send to Pandoy as-is (no tool re-run). */
  userInputs: {
    stops: Array<{
      stop: number;
      at: string;
      lon: number;
      lat: number;
      vegetation: VegetationAnswer | null;
      moisture: MoistureAnswer | null;
    }>;
    vegetationCounts: Record<string, number>;
    moistureCounts: Record<string, number>;
  };
  /** Already-sampled satellite values — Pandoy must use these, not re-fetch. */
  systemFetched: {
    observationDate: string;
    index: string;
    satellite: string;
    firstImageDate?: string | null;
    spatialResolution?: string | null;
    spatialResolutionM?: number | null;
    stops: Array<{
      stop: number;
      lon: number;
      lat: number;
      indexName: string;
      indexValue: number | null;
      ndvi: number | null;
      ndmi: number | null;
      sampleMessage?: string | null;
    }>;
  };
};

export type FarmIndexWalkControls = {
  startLocationWatch: (
    onUpdate: (update: LiveLocationUpdate) => void,
    options?: { appendTrail?: boolean; follow?: boolean }
  ) => boolean;
  stopLocationWatch: () => void;
  clearLiveLocation: () => void;
};

export interface FarmIndexWalkOverlayProps {
  available: boolean;
  farmGeometry: any;
  farmId?: string | number | null;
  farmName?: string | null;
  observationDate: string;
  index: string;
  satellite: string;
  indexMapMeta?: {
    tileUrl?: string | null;
    firstImageDate?: string | null;
    visParams?: any;
    spatialResolution?: string | null;
    spatialResolutionM?: number | null;
  };
  walkControls: FarmIndexWalkControls | null;
  onReportReady: (report: FarmWalkReport) => void;
  /** Called when walk starts/stops so parent can show bar button state */
  onWalkingChange?: (walking: boolean) => void;
  /** Register start/stop/info for observation-bar Walk button */
  onRegisterActions?: (
    actions: { start: () => void; stop: () => void; showInfo: () => void } | null
  ) => void;
}

const VEG_OPTIONS: { id: VegetationAnswer; label: string }[] = [
  { id: 'healthy', label: 'Healthy' },
  { id: 'average', label: 'Average' },
  { id: 'stressed', label: 'Stressed' },
  { id: 'bare', label: 'Bare / poor' },
  { id: 'skip', label: 'Skip' },
];

const MOI_OPTIONS: { id: MoistureAnswer; label: string }[] = [
  { id: 'wet', label: 'Wet' },
  { id: 'ok', label: 'OK' },
  { id: 'dry', label: 'Dry' },
  { id: 'skip', label: 'Skip' },
];

const SAMPLE_MIN_INTERVAL_MS = 2500;

/** Prompt spacing matches satellite pixel size: ~10 m (S2/S1) or ~30 m (Landsat). */
function movePromptMeters(
  satellite: string,
  spatialResolutionM?: number | null
): number {
  if (typeof spatialResolutionM === 'number' && Number.isFinite(spatialResolutionM) && spatialResolutionM > 0) {
    return spatialResolutionM >= 25 ? 30 : 10;
  }
  const sat = (satellite || '').toUpperCase();
  if (sat === 'L8' || sat === 'LANDSAT' || sat === 'L9') return 30;
  return 10;
}

function classifyIndex(index: string, value: number | null, vis?: { min?: number; max?: number }): string {
  if (value == null || !Number.isFinite(value)) return 'No data';
  const min = typeof vis?.min === 'number' ? vis.min : 0;
  const max = typeof vis?.max === 'number' ? vis.max : 1;
  const t = max > min ? (value - min) / (max - min) : 0.5;
  if (t < 0.33) return 'Low';
  if (t < 0.66) return 'Medium';
  return 'High';
}

function countAnswers(stops: FarmWalkStop[], key: 'vegetation' | 'moisture') {
  const out: Record<string, number> = {};
  for (const s of stops) {
    const v = s[key];
    if (!v || v === 'skip') continue;
    out[v] = (out[v] || 0) + 1;
  }
  return out;
}

export default function FarmIndexWalkOverlay({
  available,
  farmGeometry,
  farmId,
  farmName,
  observationDate,
  index,
  satellite,
  indexMapMeta,
  walkControls,
  onReportReady,
  onWalkingChange,
  onRegisterActions,
}: FarmIndexWalkOverlayProps) {
  const { isDark } = useTheme();
  const [walking, setWalking] = useState(false);
  const [pos, setPos] = useState<{ lon: number; lat: number; accuracy?: number | null } | null>(null);
  const [insideFarm, setInsideFarm] = useState(false);
  const [liveSample, setLiveSample] = useState<{
    value: number | null;
    ndvi: number | null;
    ndmi: number | null;
    message?: string | null;
  } | null>(null);
  const [sampling, setSampling] = useState(false);
  const [promptOpen, setPromptOpen] = useState(false);
  const [vegetation, setVegetation] = useState<VegetationAnswer | null>(null);
  const [moisture, setMoisture] = useState<MoistureAnswer | null>(null);
  const [stops, setStops] = useState<FarmWalkStop[]>([]);
  const [startedAt, setStartedAt] = useState<string | null>(null);
  const [reportPreview, setReportPreview] = useState<FarmWalkReport | null>(null);
  const [guideOpen, setGuideOpen] = useState(false);
  const [guideMode, setGuideMode] = useState<'info' | 'start'>('info');

  const lastSampleAtRef = useRef(0);
  const lastPromptAtRef = useRef<{ lon: number; lat: number } | null>(null);
  const pendingSampleRef = useRef<FarmWalkStop['satellite'] | null>(null);
  const promptOpenRef = useRef(false);
  const stopsRef = useRef<FarmWalkStop[]>([]);
  const startedAtRef = useRef<string | null>(null);

  useEffect(() => {
    stopsRef.current = stops;
  }, [stops]);
  useEffect(() => {
    startedAtRef.current = startedAt;
  }, [startedAt]);

  const optical =
    satellite === 'S2' ||
    satellite === 'L8' ||
    satellite === 'ALL' ||
    (index || '').toLowerCase() === 'radar_soil_moisture';

  const promptDistanceM = movePromptMeters(
    satellite,
    indexMapMeta?.spatialResolutionM
  );

  const shell = isDark
    ? 'bg-gray-900/95 border-gray-700 text-gray-100'
    : 'bg-white/95 border-gray-200 text-gray-900';
  const muted = isDark ? 'text-gray-400' : 'text-gray-500';
  const btnPrimary = isDark
    ? 'bg-emerald-700 hover:bg-emerald-600 text-white'
    : 'bg-[#008540] hover:bg-[#0f5a2e] text-white';
  const btnGhost = isDark
    ? 'border-gray-600 text-gray-200 hover:bg-gray-800'
    : 'border-gray-300 text-gray-700 hover:bg-gray-50';

  const fetchSample = useCallback(
    async (lon: number, lat: number) => {
      const now = Date.now();
      if (now - lastSampleAtRef.current < SAMPLE_MIN_INTERVAL_MS) return null;
      lastSampleAtRef.current = now;
      setSampling(true);
      try {
        const idx = (index || 'ndvi').toLowerCase();
        // Radar soil moisture uses a different endpoint; still collect ground notes + try optical NDVI/NDMI.
        const useOpticalCompanions = optical || idx === 'radar_soil_moisture';
        const sampleIndex =
          idx === 'radar_soil_moisture' ? 'ndvi' : idx;
        const sampleSatellite =
          idx === 'radar_soil_moisture' || satellite === 'ALL' ? 'S2' : satellite;
        const also_sample = useOpticalCompanions ? ['ndvi', 'ndmi'] : [];
        const { data } = await apiClient.post('/indexheatmap/sample/', {
          lon,
          lat,
          observation_date: observationDate,
          index: sampleIndex,
          satellite: sampleSatellite,
          farm_geometry: farmGeometry,
          also_sample,
        });
        const companions = data?.companions || {};
        const result = {
          index: sampleIndex,
          value: data?.value ?? null,
          ndvi:
            sampleIndex === 'ndvi'
              ? data?.value ?? null
              : companions.ndvi ?? null,
          ndmi:
            sampleIndex === 'ndmi'
              ? data?.value ?? null
              : companions.ndmi ?? null,
          observationDate,
          firstImageDate: data?.first_image_date ?? null,
          message: data?.message ?? null,
        };
        setLiveSample({
          value: result.value,
          ndvi: result.ndvi,
          ndmi: result.ndmi,
          message: result.message,
        });
        pendingSampleRef.current = result;
        return result;
      } catch (e) {
        console.warn('[FarmWalk] sample failed', e);
        setLiveSample({
          value: null,
          ndvi: null,
          ndmi: null,
          message: 'Could not sample satellite data at this spot.',
        });
        return null;
      } finally {
        setSampling(false);
      }
    },
    [farmGeometry, index, observationDate, optical, satellite]
  );

  const stopWalk = useCallback(
    (opts?: { buildReport?: boolean }) => {
      walkControls?.stopLocationWatch();
      setWalking(false);
      onWalkingChange?.(false);
      promptOpenRef.current = false;
      setPromptOpen(false);

      const currentStops = stopsRef.current;
      const started = startedAtRef.current;
      if (opts?.buildReport && started && currentStops.length > 0) {
        const endedAt = new Date().toISOString();
        const vegCounts = countAnswers(currentStops, 'vegetation');
        const moiCounts = countAnswers(currentStops, 'moisture');
        const report: FarmWalkReport = {
          type: 'farm_index_walk',
          farmId: farmId ?? null,
          farmName: farmName ?? null,
          startedAt: started,
          endedAt,
          observationDate,
          index,
          satellite,
          indexMap: {
            tileUrl: indexMapMeta?.tileUrl ?? null,
            firstImageDate: indexMapMeta?.firstImageDate ?? null,
            visParams: indexMapMeta?.visParams,
            spatialResolution: indexMapMeta?.spatialResolution ?? null,
          },
          stops: currentStops,
          summary: {
            stopCount: currentStops.length,
            answeredCount: currentStops.filter((s) => s.vegetation || s.moisture).length,
            vegetation: vegCounts,
            moisture: moiCounts,
          },
          userInputs: {
            stops: currentStops.map((s, i) => ({
              stop: i + 1,
              at: s.at,
              lon: s.lon,
              lat: s.lat,
              vegetation: s.vegetation ?? null,
              moisture: s.moisture ?? null,
            })),
            vegetationCounts: vegCounts,
            moistureCounts: moiCounts,
          },
          systemFetched: {
            observationDate,
            index,
            satellite,
            firstImageDate: indexMapMeta?.firstImageDate ?? null,
            spatialResolution: indexMapMeta?.spatialResolution ?? null,
            spatialResolutionM: indexMapMeta?.spatialResolutionM ?? null,
            stops: currentStops.map((s, i) => ({
              stop: i + 1,
              lon: s.lon,
              lat: s.lat,
              indexName: s.satellite.index,
              indexValue: s.satellite.value,
              ndvi: s.satellite.ndvi,
              ndmi: s.satellite.ndmi,
              sampleMessage: s.satellite.message ?? null,
            })),
          },
        };
        setReportPreview(report);
      }
    },
    [
      farmId,
      farmName,
      index,
      indexMapMeta,
      observationDate,
      onWalkingChange,
      satellite,
      walkControls,
    ]
  );

  const showWalkInfo = useCallback(() => {
    setGuideMode('info');
    setGuideOpen(true);
  }, []);

  const requestStartWalk = useCallback(() => {
    if (!available || !walkControls) return;
    setGuideMode('start');
    setGuideOpen(true);
  }, [available, walkControls]);

  const beginWalk = useCallback(() => {
    if (!available || !walkControls) return;
    setGuideOpen(false);
    setStops([]);
    setReportPreview(null);
    setLiveSample(null);
    setPos(null);
    lastPromptAtRef.current = null;
    pendingSampleRef.current = null;
    setStartedAt(new Date().toISOString());

    const ok = walkControls.startLocationWatch(
      (update) => {
        const { longitude, latitude, accuracy } = update;
        setPos({ lon: longitude, lat: latitude, accuracy });
        const inside = pointInFarmGeometry(longitude, latitude, farmGeometry);
        setInsideFarm(inside);
        void fetchSample(longitude, latitude);

        if (promptOpenRef.current) return;
        if (!inside) return;

        const last = lastPromptAtRef.current;
        if (!last) {
          lastPromptAtRef.current = { lon: longitude, lat: latitude };
          return;
        }
        const moved = haversineMeters(last.lon, last.lat, longitude, latitude);
        const stepM = movePromptMeters(satellite, indexMapMeta?.spatialResolutionM);
        if (moved >= stepM) {
          lastPromptAtRef.current = { lon: longitude, lat: latitude };
          setVegetation(null);
          setMoisture(null);
          promptOpenRef.current = true;
          setPromptOpen(true);
        }
      },
      { appendTrail: true, follow: true }
    );

    if (ok) {
      setWalking(true);
      onWalkingChange?.(true);
    }
  }, [
    available,
    farmGeometry,
    fetchSample,
    indexMapMeta?.spatialResolutionM,
    onWalkingChange,
    satellite,
    walkControls,
  ]);

  // Stop when Index Map becomes unavailable
  useEffect(() => {
    if (!available && walking) {
      stopWalk({ buildReport: false });
      walkControls?.clearLiveLocation();
    }
  }, [available, walking, stopWalk, walkControls]);

  // Register start/stop/info for observation bar
  useEffect(() => {
    if (!onRegisterActions) return;
    onRegisterActions({
      start: requestStartWalk,
      stop: () => stopWalk({ buildReport: true }),
      showInfo: showWalkInfo,
    });
    return () => onRegisterActions(null);
  }, [onRegisterActions, requestStartWalk, showWalkInfo, stopWalk]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      walkControls?.stopLocationWatch();
    };
  }, [walkControls]);

  const savePrompt = () => {
    if (!pos) {
      promptOpenRef.current = false;
      setPromptOpen(false);
      return;
    }
    const sat = pendingSampleRef.current || {
      index,
      value: liveSample?.value ?? null,
      ndvi: liveSample?.ndvi ?? null,
      ndmi: liveSample?.ndmi ?? null,
      observationDate,
      firstImageDate: indexMapMeta?.firstImageDate ?? null,
      message: liveSample?.message ?? null,
    };
    const stop: FarmWalkStop = {
      id: `${Date.now()}`,
      lon: pos.lon,
      lat: pos.lat,
      at: new Date().toISOString(),
      insideFarm: true,
      vegetation: vegetation || 'skip',
      moisture: moisture || 'skip',
      satellite: sat,
    };
    setStops((prev) => [...prev, stop]);
    promptOpenRef.current = false;
    setPromptOpen(false);
    setVegetation(null);
    setMoisture(null);
  };

  const dismissPrompt = () => {
    promptOpenRef.current = false;
    setPromptOpen(false);
  };

  const sendReportToPandoy = () => {
    if (!reportPreview) return;
    onReportReady(reportPreview);
    setReportPreview(null);
    walkControls?.clearLiveLocation();
  };

  if (!available) return null;

  const idxLabel = (index || 'index').toUpperCase();
  const classLabel = classifyIndex(index, liveSample?.value ?? null, indexMapMeta?.visParams);

  return (
    <>
      {/* Live feedback chip */}
      {walking && (
        <div
          className={`absolute left-14 right-3 sm:left-[4.5rem] sm:right-[6rem] z-[12] bottom-[11.5rem] sm:bottom-[12rem] rounded-xl border shadow-lg backdrop-blur-sm px-3 py-2 ${shell}`}
          role="status"
          aria-live="polite"
        >
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 text-xs font-semibold">
                <span className="inline-flex h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                Walk mode
                <span className={`font-normal ${muted}`}>· {stops.length} stop{stops.length === 1 ? '' : 's'}</span>
              </div>
              <p className={`text-[11px] mt-0.5 ${muted}`}>
                {!pos
                  ? 'Getting GPS…'
                  : !insideFarm
                    ? 'You are outside this farm boundary'
                    : liveSample?.message && liveSample.value == null
                      ? liveSample.message
                      : `${idxLabel} here: ${
                          liveSample?.value != null ? liveSample.value.toFixed(2) : '…'
                        } · ${classLabel}${
                          optical && liveSample
                            ? ` · NDVI ${liveSample.ndvi != null ? liveSample.ndvi.toFixed(2) : '—'} · NDMI ${
                                liveSample.ndmi != null ? liveSample.ndmi.toFixed(2) : '—'
                              }`
                            : ''
                        }`}
                {sampling ? ' · sampling…' : ''}
              </p>
            </div>
            <button
              type="button"
              onClick={() => stopWalk({ buildReport: true })}
              className={`shrink-0 text-[11px] font-semibold px-2.5 py-1 rounded-md border ${btnGhost}`}
            >
              End walk
            </button>
          </div>
        </div>
      )}

      {/* Goal / objective / steps guide */}
      {guideOpen && !walking && (
        <div className="absolute inset-0 z-[30] flex items-end sm:items-center justify-center bg-black/40 p-3">
          <div
            className={`w-full max-w-md rounded-2xl border shadow-xl p-4 space-y-3 max-h-[85vh] overflow-y-auto ${shell}`}
            role="dialog"
            aria-labelledby="walk-guide-title"
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 id="walk-guide-title" className="text-sm font-bold">
                  Index Map Walk mode
                </h3>
                <p className={`text-[11px] mt-0.5 ${muted}`}>
                  Ground-check your loaded Index Map while you walk the farm.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setGuideOpen(false)}
                className={`h-7 w-7 rounded-md border flex items-center justify-center shrink-0 ${btnGhost}`}
                aria-label="Close"
              >
                <i className="ri-close-line" />
              </button>
            </div>

            <div
              className={`rounded-lg border px-3 py-2.5 space-y-1.5 ${
                isDark ? 'border-emerald-800/60 bg-emerald-950/30' : 'border-emerald-100 bg-emerald-50/80'
              }`}
            >
              <p className="text-[11px] font-semibold">
                <i className="ri-flag-line mr-1" />
                Goal
              </p>
              <p className={`text-[11px] leading-snug ${muted}`}>
                Capture what the crop and moisture look like <strong className={isDark ? 'text-gray-200' : 'text-gray-800'}>today on the ground</strong>,
                at the same spots the Index Map is showing from satellite.
              </p>
            </div>

            <div
              className={`rounded-lg border px-3 py-2.5 space-y-1.5 ${
                isDark ? 'border-sky-800/60 bg-sky-950/30' : 'border-sky-100 bg-sky-50/80'
              }`}
            >
              <p className="text-[11px] font-semibold">
                <i className="ri-focus-3-line mr-1" />
                Objective
              </p>
              <p className={`text-[11px] leading-snug ${muted}`}>
                Build a short walk report with your answers and the satellite values sampled during
                the walk, then send it to{' '}
                <strong className={isDark ? 'text-gray-200' : 'text-gray-800'}>Pandoy AI</strong> to
                compare ground notes with the Index Map.
              </p>
            </div>

            <div>
              <p className="text-[11px] font-semibold mb-2">
                <i className="ri-list-ordered-2 mr-1" />
                Steps
              </p>
              <ol className={`space-y-2 text-[11px] leading-snug ${muted}`}>
                <li className="flex gap-2">
                  <span
                    className={`shrink-0 h-5 w-5 rounded-full text-[10px] font-bold flex items-center justify-center ${
                      isDark ? 'bg-gray-800 text-emerald-300' : 'bg-emerald-100 text-emerald-800'
                    }`}
                  >
                    1
                  </span>
                  <span>
                    Open a <strong className={isDark ? 'text-gray-200' : 'text-gray-800'}>clear Index Map</strong> (not fully cloud-masked) for this farm.
                  </span>
                </li>
                <li className="flex gap-2">
                  <span
                    className={`shrink-0 h-5 w-5 rounded-full text-[10px] font-bold flex items-center justify-center ${
                      isDark ? 'bg-gray-800 text-emerald-300' : 'bg-emerald-100 text-emerald-800'
                    }`}
                  >
                    2
                  </span>
                  <span>
                    Tap <strong className={isDark ? 'text-gray-200' : 'text-gray-800'}>Start walk</strong> and allow location access. Stay inside the farm boundary.
                  </span>
                </li>
                <li className="flex gap-2">
                  <span
                    className={`shrink-0 h-5 w-5 rounded-full text-[10px] font-bold flex items-center justify-center ${
                      isDark ? 'bg-gray-800 text-emerald-300' : 'bg-emerald-100 text-emerald-800'
                    }`}
                  >
                    3
                  </span>
                  <span>
                    Walk the field. As you move (~{promptDistanceM} m, one satellite pixel), answer{' '}
                    <strong className={isDark ? 'text-gray-200' : 'text-gray-800'}>vegetation</strong> and{' '}
                    <strong className={isDark ? 'text-gray-200' : 'text-gray-800'}>moisture</strong> for today. Satellite values are sampled at your point.
                  </span>
                </li>
                <li className="flex gap-2">
                  <span
                    className={`shrink-0 h-5 w-5 rounded-full text-[10px] font-bold flex items-center justify-center ${
                      isDark ? 'bg-gray-800 text-emerald-300' : 'bg-emerald-100 text-emerald-800'
                    }`}
                  >
                    4
                  </span>
                  <span>
                    Tap <strong className={isDark ? 'text-gray-200' : 'text-gray-800'}>End walk</strong>, review the report with the Index Map, then{' '}
                    <strong className={isDark ? 'text-gray-200' : 'text-gray-800'}>Send to Pandoy AI</strong> for validation.
                  </span>
                </li>
              </ol>
            </div>

            <p className={`text-[10px] leading-snug ${muted}`}>
              Tip: Use a phone outdoors with GPS on. This is a light field check — not a full offline scouting app.
            </p>

            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => setGuideOpen(false)}
                className={`text-[11px] px-3 py-1.5 rounded-md border ${btnGhost}`}
              >
                {guideMode === 'start' ? 'Not now' : 'Close'}
              </button>
              {guideMode === 'start' && (
                <button
                  type="button"
                  onClick={beginWalk}
                  className={`text-[11px] px-3 py-1.5 rounded-md ${btnPrimary}`}
                >
                  <i className="ri-walk-line mr-1" />
                  Start walking
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Movement Q&A */}
      {promptOpen && (
        <div className="absolute inset-0 z-[30] flex items-end sm:items-center justify-center bg-black/35 p-3">
          <div className={`w-full max-w-md rounded-2xl border shadow-xl p-4 space-y-3 ${shell}`}>
            <div>
              <h3 className="text-sm font-bold">At this spot — today&apos;s conditions?</h3>
              <p className={`text-[11px] mt-0.5 ${muted}`}>
                Move a bit, then answer vegetation and moisture. Satellite values are saved for Pandoy AI.
              </p>
            </div>

            <div>
              <p className="text-[11px] font-semibold mb-1.5">Vegetation</p>
              <div className="flex flex-wrap gap-1.5">
                {VEG_OPTIONS.map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => setVegetation(o.id)}
                    className={`text-[11px] px-2.5 py-1 rounded-full border ${
                      vegetation === o.id
                        ? btnPrimary
                        : btnGhost
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-[11px] font-semibold mb-1.5">Moisture</p>
              <div className="flex flex-wrap gap-1.5">
                {MOI_OPTIONS.map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => setMoisture(o.id)}
                    className={`text-[11px] px-2.5 py-1 rounded-full border ${
                      moisture === o.id
                        ? btnPrimary
                        : btnGhost
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>

            {(liveSample?.ndvi != null || liveSample?.ndmi != null || liveSample?.value != null) && (
              <p className={`text-[10px] ${muted}`}>
                Satellite at this point — {idxLabel}:{' '}
                {liveSample?.value != null ? liveSample.value.toFixed(2) : '—'}
                {optical
                  ? ` · NDVI ${liveSample?.ndvi != null ? liveSample.ndvi.toFixed(2) : '—'} · NDMI ${
                      liveSample?.ndmi != null ? liveSample.ndmi.toFixed(2) : '—'
                    }`
                  : ''}
              </p>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={dismissPrompt} className={`text-[11px] px-3 py-1.5 rounded-md border ${btnGhost}`}>
                Later
              </button>
              <button
                type="button"
                onClick={savePrompt}
                disabled={!vegetation && !moisture}
                className={`text-[11px] px-3 py-1.5 rounded-md disabled:opacity-50 ${btnPrimary}`}
              >
                Save stop
              </button>
            </div>
          </div>
        </div>
      )}

      {/* End report → Pandoy */}
      {reportPreview && (
        <div className="absolute inset-0 z-[30] flex items-end sm:items-center justify-center bg-black/40 p-3">
          <div className={`w-full max-w-md rounded-2xl border shadow-xl p-4 space-y-3 ${shell}`}>
            <div>
              <h3 className="text-sm font-bold">Walk report ready</h3>
              <p className={`text-[11px] mt-0.5 ${muted}`}>
                {reportPreview.summary.stopCount} stop
                {reportPreview.summary.stopCount === 1 ? '' : 's'} on {observationDate}{' '}
                {idxLabel} map
                {reportPreview.indexMap.firstImageDate
                  ? ` (${reportPreview.indexMap.firstImageDate})`
                  : ''}
                . Send your answers and satellite samples to Pandoy AI for comparison.
              </p>
            </div>
            <ul className={`text-[11px] space-y-1 max-h-40 overflow-auto ${muted}`}>
              {reportPreview.stops.map((s, i) => (
                <li key={s.id}>
                  #{i + 1} veg={s.vegetation || '—'} · moist={s.moisture || '—'}
                  {s.satellite.ndvi != null ? ` · NDVI ${s.satellite.ndvi.toFixed(2)}` : ''}
                  {s.satellite.ndmi != null ? ` · NDMI ${s.satellite.ndmi.toFixed(2)}` : ''}
                </li>
              ))}
            </ul>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setReportPreview(null);
                  walkControls?.clearLiveLocation();
                }}
                className={`text-[11px] px-3 py-1.5 rounded-md border ${btnGhost}`}
              >
                Dismiss
              </button>
              <button type="button" onClick={sendReportToPandoy} className={`text-[11px] px-3 py-1.5 rounded-md ${btnPrimary}`}>
                <i className="ri-brain-line mr-1" />
                Send to Pandoy AI
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
